import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import InputAdornment from "@material-ui/core/InputAdornment";
import { useCallback, useEffect, useState } from "react";
import { getAllForAdmin, update as updateVendorService } from "src/api/vendorServices";
import { debounce } from "src/helpers/debounce";
import { IconButton } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { Link } from "react-router-dom";
import { Chip } from "src/components/commons/Chip";
import { Alert } from "@material-ui/lab";
import { Main } from "src/components/commons/Main";
import { Typography } from "@material-ui/core";
import { Checkbox } from "@material-ui/core";

const statusMap = { IN_PROGRESS: "In Progress", ACTIVE: "Active", INACTIVE: "Inactive" };

function Row({ row, onContractStatusChange, updating }) {
    return (
        <TableRow key={row.id}>
            <TableCell padding="checkbox">
                <Checkbox
                    checked={!!row.contract_status}
                    disabled={updating}
                    onChange={e => onContractStatusChange(row, e.target.checked)}
                    color="primary"
                />
            </TableCell>
            <TableCell className="pl-4">{row.name}</TableCell>
            <TableCell>{row.creator ? [row.creator.firstName, row.creator.lastName].filter(Boolean).join(" ") : 'N/A'}</TableCell>
            <TableCell>{row.creator?.email || 'N/A'}</TableCell>
            <TableCell>
                <Chip classes={"text-center"} status={row.status}>
                    {statusMap[row.status]}
                </Chip>
            </TableCell>
            <TableCell>{new Intl.DateTimeFormat(navigator.language).format(new Date(row.createdAt))}</TableCell>
            <TableCell>{new Intl.DateTimeFormat(navigator.language).format(new Date(row.updatedAt))}</TableCell>
            <TableCell>
                <div className="flex gap-2">
                    <Button color="primary" component={Link} to={`/application/vendor/${row.id}`}>
                        View
                    </Button>
                </div>
            </TableCell>
        </TableRow>
    );
}

export function VendorApplications() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(-1);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [search, setSearch] = useState("");
    const [deferredSearch, setDeferredSearch] = useState("");
    const [error, setError] = useState(null);
    const [isExporting, setExporting] = useState(false);
    const [footerContractStatus, setFooterContractStatus] = useState(false);
    const [footerContractLoading, setFooterContractLoading] = useState(false);
    const [footerContractAlert, setFooterContractAlert] = useState(null);
    const [rowUpdatingId, setRowUpdatingId] = useState(null);
    const [rowAlert, setRowAlert] = useState(null);

    const updateSearch = useCallback(
        debounce(function _updateSearch(search) {
            setDeferredSearch(search);
        }, 500),
        []
    );

    useEffect(() => {
        updateSearch(search);
    }, [search]);

    useEffect(() => {
        const options = { limit: perPage, page: currentPage, join: "creator" };
        if (search) {
            options.s = JSON.stringify({
                $or: [
                    { name: { $contL: search } },
                    { "creator.firstName": { $contL: search } },
                    { "creator.lastName": { $contL: search } },
                    { "creator.email": { $contL: search } },
                ],
            });
        }
        (async () => {
            try {
                setError(null);
                setLoading(true);
                const res = await getAllForAdmin(options);
                setRows(res.data);
                setTotal(res.total);
            } catch (err) {
                console.error("Unable to fetch vendor services", err);
                setError("Unable to load");
            } finally {
                setLoading(false);
            }
        })();
    }, [currentPage, deferredSearch, perPage]);

    const handleExport = async () => {
        try {
            setExporting(true);
            const res = await getAllForAdmin({ limit: 1000 });
            const csvContent = [
                ["Service Name", "User Name", "User Email", "Status", "Created At", "Last Updated"],
                ...res.data.map(service => [
                    service.name,
                    [service.creator?.firstName, service.creator?.lastName].filter(Boolean).join(" ") || "N/A",
                    service.creator?.email || "N/A",
                    statusMap[service.status],
                    new Intl.DateTimeFormat(navigator.language).format(new Date(service.createdAt)),
                    new Intl.DateTimeFormat(navigator.language).format(new Date(service.updatedAt))
                ])
            ].map(row => row.join(",")).join("\n");
            
            const blob = new Blob([csvContent], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "InvestorMatch.ai-Vendor-Services.csv";
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error("Unable to export vendor services", err);
            setError("Unable to export");
        } finally {
            setExporting(false);
        }
    };

    const handleContractStatusChange = async (row, checked) => {
        setRowUpdatingId(row.id);
        setRowAlert(null);
        try {
            await updateVendorService({ ...row, contract_status: checked });
            setRows(prevRows => prevRows.map(r => r.id === row.id ? { ...r, contract_status: checked } : r));
            setRowAlert({ type: 'success', message: 'Contract status updated successfully' });
        } catch (err) {
            setRowAlert({ type: 'error', message: 'Failed to update contract status' });
        } finally {
            setRowUpdatingId(null);
        }
    };

    // Auto-dismiss rowAlert after 3 seconds
    useEffect(() => {
        if (rowAlert) {
            const timer = setTimeout(() => setRowAlert(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [rowAlert]);

    return (
        <Main>
            <TableContainer className="mt-4" component={Paper}>
                <Box className="flex justify-between items-center p-4">
                    <Box className="text-xl flex gap-4">
                        Vendor Services
                        <Box className="text-xl">
                            <Button
                                variant="outlined"
                                color="primary"
                                disabled={isExporting}
                                onClick={handleExport}
                            >
                                {isExporting ? "Exporting" : "Export CSV"}
                            </Button>
                        </Box>
                    </Box>

                    <Box>
                        {loading && "Loading..."}
                        {error && <Box className="text-red-600">{error}</Box>}
                    </Box>
                    <Box>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Search user / application"
                            type="text"
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setSearch("")}
                                        >
                                            <Close />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </Box>
                <Table aria-label="Vendor services table">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" style={{ width: 150, paddingLeft: 10 }}>Contract Status</TableCell>
                            <TableCell>Service Name</TableCell>
                            <TableCell>User Name</TableCell>
                            <TableCell>User Email</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Created At</TableCell>
                            <TableCell>Last Updated</TableCell>
                            <TableCell style={{ width: 200 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    {rowAlert && (
                        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>
                            <Alert severity={rowAlert.type} variant="filled">{rowAlert.message}</Alert>
                        </div>
                    )}
                    <TableBody>
                        {error && (
                            <TableRow>
                                <TableCell colSpan={7}>{error}</TableCell>
                            </TableRow>
                        )}
                        {rows.length <= 0 && (
                            <TableRow>
                                <TableCell colSpan={7}>{loading ? "Loading..." : "No data"}</TableCell>
                            </TableRow>
                        )}
                        {rows.length > 0 &&
                            rows.map((row, idx) => (
                                <Row
                                    key={`row-${row.id || idx}`}
                                    row={row}
                                    updating={rowUpdatingId === row.id}
                                    onContractStatusChange={handleContractStatusChange}
                                />
                            ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell></TableCell>
                            <TablePagination
                                rowsPerPageOptions={[20, 50, 100]}
                                count={total}
                                rowsPerPage={perPage}
                                page={currentPage - 1}
                                SelectProps={{
                                    inputProps: { "aria-label": "rows per page" },
                                    native: true,
                                }}
                                onPageChange={(e, newPage) => {
                                    setCurrentPage(newPage + 1);
                                }}
                                onRowsPerPageChange={(e) => {
                                    setPerPage(parseInt(e.target.value, 10));
                                    setCurrentPage(1);
                                }}
                            />
                            <TableCell align="right">
                                
                                {footerContractAlert && (
                                    <Alert severity={footerContractAlert.type} style={{ marginTop: 8 }}>{footerContractAlert.message}</Alert>
                                )}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Main>
    );
} 